import { demoModeAtom, reportDemoModeAtom } from '@/global-store';
import ProductCards from '@components/ProductCards';
import UserTour from '@components/UserTour.jsx';
import { useAtom } from 'jotai';
import { Fragment, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import PageTitle from '@/components/PageTitle';
import DomainOverviewSection from './components/DomainOverviewSection';
import { Typography } from '@eye-able/ui';
import HomeApplicationsBanner from './components/HomeApplicationsBanner';
import LoadingSpinner from '@components/LoadingSpinner';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { T } from '@/i18n/T';

const Home = () => {
  const { t } = useInlineTranslation();

  const [demoMode] = useAtom(demoModeAtom);
  const [reportDemoMode] = useAtom(reportDemoModeAtom);

  if (demoMode) {
    return <Navigate replace={false} to='/demo' />;
  }

  // `useSuspenseQuery` has no `enabled`, so in report demo mode — where there is
  // nothing to report on — the way to not fetch is to not render the section.
  const hasReports = !reportDemoMode;

  return (
    <Fragment>
      <PageTitle
        heading={<T k='homepage' />}
        description={hasReports ? undefined : <T k='dashboardText1' />}
      />

      <div className='eye:flex eye:flex-col eye:gap-l'>
        <section
          aria-label={t('allApplications')}
          className='eye:flex eye:flex-col eye:gap-xs'
        >
          <Typography tag='h2' variant='headline-s'>
            <T k='allApplications' />
          </Typography>
          <HomeApplicationsBanner />
        </section>

        {hasReports ? (
          /*
           * Its own boundary rather than the layout's: this section's query
           * downloads a report per domain, and behind the shared one the
           * greeting and the applications above sat behind that wait too. Here
           * they paint at once and only the overview shows a spinner.
           */
          <Suspense fallback={<LoadingSpinner />}>
            <DomainOverviewSection />
          </Suspense>
        ) : (
          <StartUsingSection />
        )}
      </div>

      <UserTour />
    </Fragment>
  );
};

function StartUsingSection() {
  const { t } = useInlineTranslation();

  return (
    <section
      id='homeStartUsingSection'
      aria-label={t('startUsingService')}
      className='eye:flex eye:flex-col eye:gap-xs'
    >
      <Typography tag='h2' variant='headline-s'>
        <T k='startUsingService' />
      </Typography>
      <ProductCards />
    </section>
  );
}

export default Home;
